import { Box3 } from '@technology/web3d-plugin-threejs';
import { type QmDecoder } from './processing/qm-decoder';

/**
 * Bounds the terrain model publishes, which Web3D fits its clipping planes to.
 *
 * Tiles are culled against the frustum those planes produce, so the bounds
 * must contain every root tile's box, loaded or not. Otherwise the tile under
 * the camera can be culled before it ever loads: an unloaded root has only a
 * placeholder height of 0, which terrain far from sea level would exclude.
 */
export function computeTerrainBounds(
  rootBoxes: readonly Box3[],
  heights: Pick<QmDecoder, 'minHeight' | 'maxHeight' | 'baseHeight'>
): Box3 {
  const bounds = new Box3();
  for (const box of rootBoxes) {
    bounds.union(box);
  }

  // Decoder heights are raw tile header values; vertices at the initial base
  // height are drawn at baseHeight instead, so include it explicitly.
  const knownHeights = [
    heights.minHeight,
    heights.maxHeight,
    heights.baseHeight,
  ].filter((height): height is number => height !== null);
  if (knownHeights.length !== 0) {
    bounds.min.z = Math.min(bounds.min.z, ...knownHeights);
    bounds.max.z = Math.max(bounds.max.z, ...knownHeights);
  }

  return bounds;
}
